import { copy } from "../../config/copy.js";
import { h } from "../../core/dom.js";
import { drawCard, shareImage, shareText, statsText } from "../share.js";
import { swipeToClose } from "./sheet.js";

const format = new Intl.DateTimeFormat("it-IT", { dateStyle: "full", timeStyle: "short" });

function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Anteprima non leggibile"));
    image.src = source;
  });
}

export function bars(ranking, limit = 6) {
  return ranking.slice(0, limit).map((entry) => h("div", { class: "detail-bar" },
    h("span", {}, `${entry.emoji || ""} ${entry.label_it || entry.id}`),
    h("div", { class: "bar" }, h("div", { class: "bar-fill", style: `width:${Math.round(entry.probability * 100)}%` })),
    h("span", { class: "muted" }, `${Math.round(entry.probability * 100)}%`)));
}

export function historySheet(app, { item, spec, ranking, onDeleted = () => {} }) {
  const backdrop = h("div", { class: "sheet-backdrop" });
  let release = null;
  const close = (fromBack = false) => {
    backdrop.remove();
    swipe.detach();
    if (!fromBack) release?.();
    release = null;
  };

  const text = statsText({ label: spec.label_it || item.label, emoji: spec.emoji, confidence: item.confidence,
    createdAt: item.createdAt, ranking });

  const shareTextButton = h("button", { class: "btn btn-ghost", type: "button" }, "📋 Condividi testo");
  shareTextButton.addEventListener("click", () => shareText(text, app.toast)
    .catch((error) => { app.reporter.report(error, "share-text"); app.toast(copy.shareError, "error"); }));

  const shareImageButton = h("button", { class: "btn btn-ghost", type: "button" }, "🖼️ Condividi immagine");
  shareImageButton.addEventListener("click", async () => {
    shareImageButton.disabled = true;
    try {
      const canvas = document.createElement("canvas");
      const picture = item.thumbnail ? await loadImage(item.thumbnail) : null;
      drawCard(canvas, { thumbnail: picture, label: spec.label_it || item.label, emoji: spec.emoji,
        confidence: item.confidence, createdAt: item.createdAt, ranking });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (blob) await shareImage(blob, `paws-${item.id}.png`);
    } catch (error) {
      app.reporter.report(error, "share-image");
      app.toast(copy.shareError, "error");
    } finally {
      shareImageButton.disabled = false;
    }
  });

  const actions = h("div", { class: "detail-actions" });
  const askDelete = h("button", { class: "btn btn-danger", type: "button" }, "🗑 Elimina");
  const remove = async () => {
    actions.replaceChildren(h("p", { class: "muted" }, "Sto eliminando…"));
    try {
      await app.history.remove(item.id);
      if (item.feedbackId) app.feedback.remove(item.feedbackId).catch(() => {});
      close();
      onDeleted();
    } catch (error) {
      app.reporter.report(error, "history-remove");
      app.toast("Non sono riuscito a eliminare l'analisi.", "error");
      actions.replaceChildren(askDelete);
    }
  };
  askDelete.addEventListener("click", () => {
    actions.replaceChildren(
      h("span", { class: "muted" }, "Eliminare questa analisi?"),
      h("button", { class: "btn btn-ghost", type: "button", onclick: () => actions.replaceChildren(askDelete) }, "No"),
      h("button", { class: "btn btn-danger", type: "button", onclick: remove }, "Sì, elimina"));
  });
  actions.append(askDelete);

  const body = h("div", { class: "sheet-body" },
    item.thumbnail ? h("img", { class: "sheet-photo", src: item.thumbnail, alt: "Foto analizzata" }) : null,
    h("div", { class: "verdict" },
      h("div", { class: "verdict-emoji" }, spec.emoji || "🐾"),
      h("div", {},
        h("h2", {}, `${spec.label_it || item.label}${item.uncertain ? " (incerto)" : ""}`),
        h("p", { class: "muted" }, `Confidenza ${Math.round(item.confidence * 100)}%`),
        h("p", { class: "muted" }, format.format(item.createdAt)))),
    h("dl", { class: "detail-facts" },
      h("dt", {}, "Origine"), h("dd", {}, sourceLabel(item.source)),
      h("dt", {}, "Analisi"), h("dd", {}, item.engine === "advanced" ? copy.engineAdvanced : copy.engineQuick),
      item.feedback ? h("dt", {}, "Tuo feedback") : null,
      item.feedback ? h("dd", {}, item.feedback === "ok" ? "Hai confermato" : `Hai corretto in ${item.feedback}`) : null),
    h("h3", {}, "Tutte le percentuali"),
    ...bars(ranking, 14),
    item.explanation ? h("h3", {}, "Spiegazione") : null,
    item.explanation ? h("p", {}, item.explanation) : null,
    h("div", { class: "detail-actions" }, shareTextButton, shareImageButton),
    actions);

  const sheet = h("section", { class: "sheet", role: "dialog", "aria-modal": "true", "aria-label": "Dettagli analisi" },
    h("button", { class: "sheet-close", "aria-label": "Chiudi", type: "button", onclick: () => close() }, "✕"),
    h("div", { class: "sheet-handle" }),
    body);
  backdrop.addEventListener("click", (event) => { if (event.target === backdrop) close(); });
  backdrop.append(sheet);
  document.body.append(backdrop);
  const swipe = swipeToClose(sheet, { onClose: () => close() });
  release = app.back?.push(() => close(true));
  return { element: backdrop, close };
}

export function sourceLabel(source) {
  const labels = { photo: "Foto dalla fotocamera", upload: "Foto dalla galleria", clip: "Clip registrata", video: "Video dalla galleria" };
  return labels[source] || "Foto";
}
